'use client';

import type { MetricExplodeRow } from '@slims/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

/**
 * La scomposizione per POD di una metrica (`GET /api/energy/pod-breakdown`).
 *
 * La base di riconciliazione non è un parametro: la dichiara la metrica nel
 * registro condiviso, così l'esploso legge la stessa sorgente del pannello da cui
 * si apre e il dettaglio quadra col totale. Qui si passano solo la metrica e la
 * finestra.
 */
export interface MetricBreakdown {
	metric: {
		id: string;
		label: string;
		unit: string;
		decomposition: 'sum' | 'allocated' | 'none';
		allocationBasis?: string;
		note?: string;
	};
	from: string;
	to: string;
	total: number;
	rows: MetricExplodeRow[];
	/**
	 * Se la classifica si può rileggere su un singolo livello energetico, e dove
	 * no il motivo. Lo decide il registro delle metriche, non la pagina.
	 */
	layerFilter?: { enabled: boolean; reason?: string };
	allocationBasis?: string;
	emptyReason?: string;
}

export interface UseMetricExplodeParams {
	metricId?: string | null;
	/** Giorno civile iniziale `YYYY-MM-DD`. */
	from?: string | null;
	/** Giorno civile finale `YYYY-MM-DD`, incluso. */
	to?: string | null;
	/**
	 * Restringe a una categoria di socio (`res` | `com` | `pa`), per i KPI che
	 * mostrano il consumo di un solo gruppo: senza, la somma dell'esploso non
	 * tornerebbe il totale del pannello.
	 */
	memberGroup?: string | null;
	/** Restringe ai POD di un socio, per i KPI della sua scheda. */
	memberId?: string | null;
	/**
	 * Configurazione da leggere. Assente = lo scope amministrativo corrente; va
	 * passato dove la pagina ne mostra più di una affiancate (la dashboard), o
	 * l'esploso risponderebbe con i POD della configurazione sbagliata.
	 */
	orgId?: string | null;
	enabled?: boolean;
}

/** ADR-006: backed by React Query. */
export function useMetricExplode({
	metricId,
	from,
	to,
	memberGroup,
	memberId,
	orgId: orgIdOverride,
	enabled = true,
}: UseMetricExplodeParams) {
	const scopeOrgId = useAdminScopeStore((s) => s.configId);
	const orgId = orgIdOverride ?? scopeOrgId;
	const active = !!metricId && !!from && !!to && enabled && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: [
			'metric-explode',
			orgId,
			metricId,
			from,
			to,
			memberGroup ?? null,
			memberId ?? null,
		],
		enabled: active,
		queryFn: () => {
			const params = new URLSearchParams({
				metric: metricId as string,
				from: from as string,
				to: to as string,
				orgId: orgId as string,
			});
			if (memberGroup) params.set('memberGroup', memberGroup);
			if (memberId) params.set('memberId', memberId);
			return api.get<MetricBreakdown>(`/api/energy/pod-breakdown?${params.toString()}`);
		},
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	return {
		data: data ?? null,
		isLoading: active ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento della scomposizione'
			: null,
		refetch,
	};
}
